<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import Loader from "./components/Loader.vue";
import Header from "./components/Header.vue";
import Hero from "./components/Hero.vue";
import Collection from "./components/Collection.vue";
import Story from "./components/Story.vue";
import ProductModal from "./components/ProductModal.vue";
import CartDrawer from "./components/CartDrawer.vue";
import InstallCTA from "./components/InstallCTA.vue";
import Footer from "./components/Footer.vue";
// VersionChip hidden during the public preview window — bring back once the
// storefront work resumes.
// import VersionChip from "./components/VersionChip.vue";
import { products } from "./data/products";

// Route gate. Until the storefront is finished, the public landing (/) keeps
// the loader as the only thing rendered — only /home renders the catalog,
// and it never mounts the loader. Mutually exclusive v-if on isHomeRoute
// (not a loading flag) so the catalog DOM literally does not exist on /:
// there is no way for a stale HMR listener or a stray reactivity update to
// flip the page into view on the public path.
const isHomeRoute =
  typeof window !== "undefined" && window.location.pathname === "/home";

const activeCategory = ref("all");
const activeSlug = ref<string | null>(null);
const installOpen = ref(false);

const activeProduct = computed(() => {
  if (!activeSlug.value) return null;
  return products.find((p) => p.slug === activeSlug.value) ?? null;
});

const scheduleInstallPrompt = () => {
  if (typeof window === "undefined") return;
  const seen = window.localStorage.getItem("os1.install.seen");
  if (seen) return;
  window.setTimeout(() => {
    installOpen.value = true;
    window.localStorage.setItem("os1.install.seen", "1");
  }, 8000);
};

const openProduct = (slug: string) => {
  activeSlug.value = slug;
};

const closeProduct = () => {
  activeSlug.value = null;
};

const openInstall = () => {
  installOpen.value = true;
};

const closeInstall = () => {
  installOpen.value = false;
};

const selectCategory = (id: string) => {
  activeCategory.value = id;
  if (typeof document !== "undefined") {
    document.getElementById("shop")?.scrollIntoView({ behavior: "smooth", block: "start" });
  }
};

onMounted(() => {
  document.documentElement.lang = "en";
  // On /home the loader never mounts, so onLoaderDone never fires — schedule
  // the install prompt here instead so the CTA still appears for visitors
  // who land directly on the catalog.
  if (isHomeRoute) {
    scheduleInstallPrompt();
  }
});
</script>

<template>
  <Loader v-if="!isHomeRoute" />

  <div v-if="isHomeRoute" class="page">
    <Header
      :activeCategory="activeCategory"
      @select="selectCategory"
      @open-install="openInstall"
    />
    <main>
      <Hero @open="openProduct" @open-install="openInstall" />
      <Collection
        :activeCategory="activeCategory"
        @select="selectCategory"
        @open="openProduct"
      />
      <Story @open-install="openInstall" />
    </main>
    <Footer />

    <ProductModal :product="activeProduct" @close="closeProduct" />
    <CartDrawer />
    <InstallCTA :open="installOpen" @close="closeInstall" />
  </div>

  <!-- <VersionChip /> hidden during the public preview window -->
</template>

<style scoped>
.page {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background: var(--os1-bg);
}

main {
  flex: 1;
}
</style>
